import React, { useState, useContext, useEffect } from "react";
import TableScreen from "../common/TableScreen/TableScreen";
import { useNavigate } from "react-router-dom";
import GenericModal from "../../Ui/Modal";
import ButtonComponent from "../../Ui/Button";
import PlusIcon from "assets/icons/Plus.svg";
import { getHeaders, formatDate, humanizeEnum, formatCurrency, isWarrantyExpired } from "../../utils/utils";
import { UserContext } from "../../context/UserContext";
import { searchbarVisibilty } from "../../constant/Home/Dashboard";
import { Invoices } from "../../../service";
import useApiHandler from "../../hooks/ApiHandler";
import CircularLoader from "../../Ui/Loader";
import { Button } from "@mui/material";
import { toast } from "react-hot-toast";

const PaidInvoices = ({}) => {
  const { userData } = useContext(UserContext);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const { callApi, loading } = useApiHandler();

  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewingId, setViewingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showWarrantyExpired, setShowWarrantyExpired] = useState(false);
  const navigate = useNavigate();

  const getPaidInvoices = async (opts = {}) => {
    const { silent } = opts;
    await callApi(() => Invoices.getPaidInvoices(currentPage, resultsPerPage), {
      silent,
      onSuccess: (response) => {
        if (response.data) {
          setInvoices(response.data?.results || response.data);
          setTotalCount(response.data?.count || response.data?.length || 0);
        }
      },
    });
  };

  // Handle results per page change
  const handleResultsPerPageChange = (newResultsPerPage) => {
    setResultsPerPage(newResultsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    getPaidInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resultsPerPage]);

  // Refresh paid invoices list during dashboard polling while this tab is mounted
  useEffect(() => {
    const onPoll = () => {
      getPaidInvoices({ silent: true }).catch(() => {});
    };
    window.addEventListener("dashboard:poll", onPoll);
    return () => window.removeEventListener("dashboard:poll", onPoll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resultsPerPage]);

  const filteredRows = React.useMemo(() => {
    if (!invoiceSearch) return invoices;
    const searchLower = invoiceSearch.toLowerCase();

    return invoices.filter((row) => {
      return (
        String(row.id || "").toLowerCase().includes(searchLower) ||
        String(row.jobSnapshot?.jobTitle || "").toLowerCase().includes(searchLower) ||
        String(row.jobSnapshot?.propertyAddress || "")
          .toLowerCase()
          .includes(searchLower)
      );
    });
  }, [invoiceSearch, invoices]);

  const handleViewInvoice = async (row) => {
    const id = row?.id;
    if (!id) return toast.error("Missing invoice id");

    // Back-end may provide either snake_case or camelCase depending on serializer.
    const url =
      row?.stripe_invoice_url ||
      row?.stripeInvoiceUrl ||
      row?.stripeInvoiceUrl?.url ||
      row?.stripeInvoiceUrl?.href ||
      null;

    if (!url) return toast.error("Invoice/receipt link isn't available yet.");

    setViewingId(id);
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.log("Error opening invoice URL:", e);
      toast.error("Couldn't open invoice link.");
    } finally {
      setViewingId(null);
    }
  };

  const handleDownloadInvoice = async (row) => {
    const id = row?.id;
    if (!id) return toast.error("Missing invoice id");
    setDownloadingId(id);

    const url =
      row?.stripe_invoice_url ||
      row?.stripeInvoiceUrl ||
      row?.invoicePdfUrl ||
      row?.invoice_pdf_url ||
      null;

    if (!url) {
      toast.error("Invoice download link isn't available.");
      setDownloadingId(null);
      return;
    }

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = `invoice-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloadingId(null);

    // try {
    //   await callApi(() => Invoices.getInvoiceDownloadUrl(id), {
    //     onSuccess: (response) => {
    //       const url = resolveInvoiceUrl(response?.data, row, "download");
    //       if (url) {
    //
    //       } else {
    //         toast.error("Unable to download invoice. No URL returned.");
    //       }
    //     },
    //     finallyCallback: () => setDownloadingId(null),
    //   });
    // } catch (e) {
    //   setDownloadingId(null);
    // }
  };

  const handleWarrantyClick = (row) => {
    if (isWarrantyExpired(row)) {
      setShowWarrantyExpired(true);
      return;
    }
    const jobId = row?.jobSnapshot?.jobId || row?.jobId || row?.id;
    navigate(`/warranty-service/${jobId}`.replace(/\/$/, ""), {
      state: { invoice: row },
    });
  };

  const renderCell = (row, header) => {
    switch (header) {
      case "DATE ISSUED":
        return formatDate(row?.createdAt);
      case "INVOICE ID":
        return row?.id;
      case "PROPERTY ADDRESS":
        return row?.jobSnapshot?.propertyAddress;
      case "AMOUNT":
        return formatCurrency(
          row?.amount_paid ??
            row?.amountPaid ??
            row?.total_billed ??
            row?.totalBilled ??
            row?.subtotal ??
            row?.amount
        );
      case "JOB TITLE": {
        const js = row?.jobSnapshot || {};
        // Prefer any display-friendly labels provided by backend
        const displayTitle =
          js?.jobTitleDisplay ??
          js?.job_title_display ??
          js?.subIssueTypeDisplay ??
          js?.sub_issue_type_display ??
          js?.issueTypeDisplay ??
          js?.issue_type_display ??
          null;
        if (displayTitle) return displayTitle;

        // Fallback to humanizing enum-like fields
        const rawTitle =
          js?.jobTitle ??
          js?.job_title ??
          js?.subIssueType ??
          js?.sub_issue_type ??
          js?.issueType ??
          js?.issue_type ??
          null;
        return rawTitle ? humanizeEnum(String(rawTitle)) : "N/A";
      }
      case "VENDOR":
        return row?.vendorName || "";
      case "JOB DESCRIPTION":
        return row?.jobSnapshot?.description || "No description provided";
      case "DATE PAID": {
        const paid = row?.paidAt || row?.paid_at || row?.datePaid;
        return paid ? formatDate(paid) : "N/A";
      }
      case "WARRANTY SERVICE":
        return (
          <Button
            variant="outlined"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              border: "1px solid #ECECEC",
              color: "#242424",
              whiteSpace: "nowrap",
            }}
            onClick={() => handleWarrantyClick(row)}
          >
            Warranty Service
          </Button>
        );
      case "VIEW":
        return (
          <Button
            variant="outlined"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              border: "1px solid #ECECEC",
              color: "#242424",
            }}
            disabled={viewingId === row?.id}
            href={row?.stripe_invoice_url || row?.stripeInvoiceUrl || row?.stripeInvoiceUrl?.url || row?.stripeInvoiceUrl?.href || undefined} target="_blank" rel="noopener noreferrer" component="a"
          >
            View
          </Button>
        );
      // case "DOWNLOAD":
      //   return (
      //     <Button
      //       variant="outlined"
      //       size="small"
      //       style={{
      //         borderRadius: 8,
      //         textTransform: "none",
      //         border: "1px solid #ECECEC",
      //         color: "#242424",
      //       }}
      //       disabled={downloadingId === row?.id}
      //       onClick={() => handleDownloadInvoice(row)}
      //     >
      //       Download
      //     </Button>
      //   );
      default:
        return row[header.toLowerCase()] || "N/A";
    }
  };

  return (
    <div className=" max-w-[1440px] m-auto  md:px-6 px-4 ">
      {loading ? (
        <CircularLoader minHeight="400px" />
      ) : invoices.length ? (
        <TableScreen
          title="Paid Invoices"
          description="View, download, and manage all paid invoices for your jobs."
          placeholder="Search by invoice id, property name, job title"
          headers={getHeaders(userData?.accountType || "default", "paidInvoices")}
          rows={filteredRows}
          onSearch={setInvoiceSearch}
          search={
            searchbarVisibilty[userData?.accountType || "default"]?.mainPaidInvoices
          }
          renderCell={renderCell}
          // Pagination props
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          resultsPerPage={resultsPerPage}
          onResultsPerPageChange={handleResultsPerPageChange}
          totalCount={totalCount}
          useExternalPagination={true}
        />
        ) : (
          <div className="text-center py-8 text-[#595959]">
            <p className="text-[16px] font-[500]">No paid invoices at the moment.</p>
            <p className="text-[14px] mt-2">All invoices that have been paid will appear here.</p>
          </div>
        )}

        {/* Warranty Expired Modal */}
        <GenericModal
          open={showWarrantyExpired}
          onClose={() => setShowWarrantyExpired(false)}
          title=" "
          logoDetail="Warranty Expired"
          children={
            <div className="md:p-6 p-4">
              <p className="text-center text-[#595959] text-[12px] max-w-[720px] m-auto">
                The warranty for this job has expired. If you are experiencing issues, please submit a new job request
                and we will help get your problem resolved.
              </p>
            </div>
          }
          footer={
            <div className="w-full flex justify-end">
              <ButtonComponent
                bgColor="#3CAB5C"
                color="#FFFFFF"
                onClick={() => {
                  setShowWarrantyExpired(false);
                  navigate("/post-job");
                }}
              >
                <span className="flex items-center gap-2">
                  <img src={PlusIcon} alt="plus" className="w-[14px] h-[14px]" />
                  Post a job
                </span>
              </ButtonComponent>
            </div>
          }
          maxWidth="756px"
        />
      </div>
  );
};

export default PaidInvoices;
